/* Circadia Site-Wide Search modal — behaviour  (Phase 7, Slice S8)
   ============================================================================
   Drives snippets/search-everything.liquid. A custom global-search overlay
   that COEXISTS with Dawn predictive search (it does not touch Dawn's modal).

   WHAT IT DOES
   * Opens via the Circadia header trigger ([data-search-everything-open], wired
     in S9), the "/" keyboard shortcut, or window.Circadia.search.open().
   * Type-ahead: debounced (~200ms) GET /apps/dashboard/search?q=&limit=5, then
     renders results grouped by entity_type (product, journal_article, playbook,
     wholesale_account, faq, static_page), each group capped with a
     "See all N →" affordance that opens an in-modal paginated group view
     (?type=&limit=20&offset=, "Load more").
   * Empty / no-results → the server-rendered featured-product fallback stays
     visible, so there is always a path forward.
   * A COMMITTED search (Enter, or selecting a result) is logged server-side by
     re-issuing the query with ?source=modal|mobile_modal AND fires the
     client analytics beacon window.Circadia.analytics.emit('search_performed').
   * ESC / overlay-click / close-button close it; focus is trapped while open
     and restored to the opener on close; background scroll is locked.

   CONTRACT NOTES (verified against app/src/api/search/search.ts)
   * Response: { query, total, groups: { <type>: { count, items:[ {type, title,
     snippet, url, image, subbrand, category} ] } } }. There is NO price field on
     live results — only the Liquid featured fallback shows price.
   * limit default 5 / max 20; source must be 'modal' | 'mobile_modal' to log.

   No external dependencies. IIFE, ES5-ish (var / function / string concat),
   matching analytics-emit.js / case-aggregation.js / circadia-chrome.js.
   Results are built with DOM + textContent (never innerHTML of API content),
   so server text can never be interpreted as markup. */

(function () {
  'use strict';

  // App Proxy path — Shopify appends the signed HMAC params when forwarding.
  var SEARCH_URL = '/apps/dashboard/search';

  var DEBOUNCE_MS = 200;
  var MIN_QUERY_LEN = 2;
  var OVERVIEW_LIMIT = 5; // matches API DEFAULT_LIMIT (per-group cap)
  var GROUP_LIMIT = 20; // matches API MAX_LIMIT (the "see all" page size)

  var DEFAULT_HINT = 'Search products, the journal, playbooks, and stockists.';

  // entity_type render order + display labels (mirrors SEARCH_ENTITY_TYPES).
  var TYPE_ORDER = [
    'product',
    'journal_article',
    'playbook',
    'wholesale_account',
    'faq',
    'static_page',
  ];
  var TYPE_LABEL = {
    product: 'Products',
    journal_article: 'Journal',
    playbook: 'Playbooks',
    wholesale_account: 'Stockists',
    faq: 'FAQ',
    static_page: 'Pages',
  };

  // ── element refs (resolved in init) ──────────────────────────────────────
  var root, overlay, dialog, input, results, empty, emptyMsg;

  // ── state ─────────────────────────────────────────────────────────────────
  var debounceTimer = null;
  var seq = 0; // request sequencer — drops stale responses
  var lastQuery = ''; // last query we searched (for commit + group view)
  var lastTotal = 0; // total from the last overview render (for the beacon)
  var opener = null; // element to restore focus to on close

  // in-group ("see all") view state; groupType === null means overview.
  var groupType = null;
  var groupOffset = 0;
  var groupCount = 0;
  var groupItems = [];

  // ── tiny DOM helpers ─────────────────────────────────────────────────────
  function qs(sel, el) {
    return (el || document).querySelector(sel);
  }
  function ce(tag, cls) {
    var el = document.createElement(tag);
    if (cls) el.className = cls;
    return el;
  }
  function closestMatch(node, selector) {
    if (node && typeof node.closest === 'function') return node.closest(selector);
    return null;
  }

  // 'mobile_modal' on the full-screen breakpoint, else 'modal'. Used only for
  // the committed-search source tag (must be one of those two literals).
  function sourceTag() {
    try {
      if (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) {
        return 'mobile_modal';
      }
    } catch (_) {
      /* fall through */
    }
    return 'modal';
  }

  // ── open / close ─────────────────────────────────────────────────────────
  function isOpen() {
    return !!root && root.hidden === false;
  }

  function open() {
    if (!root || isOpen()) return;
    opener = document.activeElement;
    exitGroup();
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('se-open');
    // Defer focus a tick so the element is laid out before we select it.
    window.setTimeout(function () {
      if (input) {
        try {
          input.focus();
          input.select();
        } catch (_) {
          /* ignore */
        }
      }
    }, 20);
  }

  function close() {
    if (!root || !isOpen()) return;
    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('se-open');
    if (opener && typeof opener.focus === 'function') {
      try {
        opener.focus();
      } catch (_) {
        /* ignore */
      }
    }
    opener = null;
  }

  function toggle() {
    if (isOpen()) close();
    else open();
  }

  // ── data fetch ───────────────────────────────────────────────────────────
  function fetchJson(url) {
    return fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (r) {
      if (!r.ok) throw new Error('http ' + r.status);
      return r.json();
    });
  }

  function buildUrl(params) {
    var parts = [];
    for (var key in params) {
      if (!Object.prototype.hasOwnProperty.call(params, key)) continue;
      var v = params[key];
      if (v === null || v === undefined || v === '') continue;
      parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(v));
    }
    return SEARCH_URL + (parts.length ? '?' + parts.join('&') : '');
  }

  // ── analytics ────────────────────────────────────────────────────────────
  // Fire-and-forget; never let analytics break the search UX (isolation bar).
  function emitSearchPerformed(q, count, source) {
    if (!q) return;
    try {
      if (
        window.Circadia &&
        window.Circadia.analytics &&
        typeof window.Circadia.analytics.emit === 'function'
      ) {
        window.Circadia.analytics.emit('search_performed', {
          query: q,
          results_count: typeof count === 'number' ? count : 0,
          source: source,
        });
      }
    } catch (_) {
      /* swallow */
    }
  }

  // Server-side committed-search log: re-issue the query with ?source=. limit=1
  // keeps the payload tiny — the server's per-group count(*) is independent of
  // limit, so the logged results_count stays accurate. keepalive so it survives
  // the navigation that a result-click triggers.
  function logCommitted(q, source) {
    if (!q) return;
    try {
      fetch(buildUrl({ q: q, source: source, limit: 1 }), {
        credentials: 'same-origin',
        keepalive: true,
      }).catch(function () {});
    } catch (_) {
      /* swallow */
    }
  }

  // ── overview (type-ahead) ────────────────────────────────────────────────
  function runOverview(q) {
    var mySeq = ++seq;
    lastQuery = q;
    fetchJson(buildUrl({ q: q, limit: OVERVIEW_LIMIT }))
      .then(function (data) {
        if (mySeq !== seq) return; // a newer request superseded this one
        renderOverview(data);
      })
      .catch(function () {
        if (mySeq !== seq) return;
        renderError();
      });
  }

  // Enter → committed search: refresh results WITH ?source= (server logs it) and
  // fire the client beacon.
  function commit() {
    var q = input ? input.value.trim() : '';
    if (q.length < MIN_QUERY_LEN) return;
    var source = sourceTag();
    var mySeq = ++seq;
    lastQuery = q;
    fetchJson(buildUrl({ q: q, limit: OVERVIEW_LIMIT, source: source }))
      .then(function (data) {
        emitSearchPerformed(q, data && typeof data.total === 'number' ? data.total : 0, source);
        if (mySeq !== seq) return;
        renderOverview(data);
      })
      .catch(function () {
        if (mySeq !== seq) return;
        renderError();
      });
  }

  function renderOverview(data) {
    var groups = (data && data.groups) || {};
    var total = data && typeof data.total === 'number' ? data.total : 0;
    lastTotal = total;

    if (total === 0) {
      var qLabel = (data && data.query) || lastQuery || '';
      showEmpty('No results for “' + qLabel + '”. Try a different search.');
      return;
    }

    var frag = document.createDocumentFragment();
    for (var i = 0; i < TYPE_ORDER.length; i++) {
      var type = TYPE_ORDER[i];
      var g = groups[type];
      if (!g || !g.count || !g.items || g.items.length === 0) continue;
      frag.appendChild(overviewGroupEl(type, g));
    }
    setResults(frag);
  }

  function overviewGroupEl(type, group) {
    var section = ce('section', 'se-group');

    var head = ce('div', 'se-group__head');
    var label = ce('h3', 'se-group__label');
    label.textContent = TYPE_LABEL[type] || type;
    head.appendChild(label);

    if (group.count > group.items.length) {
      var seeAll = ce('button', 'se-group__seeall');
      seeAll.setAttribute('type', 'button');
      seeAll.setAttribute('data-se-seeall', type);
      seeAll.textContent = 'See all ' + group.count + ' →';
      head.appendChild(seeAll);
    }
    section.appendChild(head);

    for (var i = 0; i < group.items.length; i++) {
      section.appendChild(resultEl(group.items[i]));
    }
    return section;
  }

  // ── in-group "see all" view ──────────────────────────────────────────────
  function enterGroup(type) {
    groupType = type;
    groupOffset = 0;
    groupCount = 0;
    groupItems = [];
    fetchGroupPage();
  }

  function exitGroup() {
    groupType = null;
    groupOffset = 0;
    groupCount = 0;
    groupItems = [];
  }

  function fetchGroupPage() {
    if (groupType === null) return;
    var type = groupType;
    var mySeq = ++seq;
    fetchJson(buildUrl({ q: lastQuery, type: type, limit: GROUP_LIMIT, offset: groupOffset }))
      .then(function (data) {
        if (mySeq !== seq || groupType !== type) return;
        var g = data && data.groups && data.groups[type];
        if (g) {
          groupCount = g.count || 0;
          var items = g.items || [];
          groupItems = groupItems.concat(items);
          groupOffset += items.length;
        }
        renderGroup();
      })
      .catch(function () {
        if (mySeq !== seq || groupType !== type) return;
        renderError();
      });
  }

  function renderGroup() {
    var frag = document.createDocumentFragment();

    var head = ce('div', 'se-groupview__head');
    var back = ce('button', 'se-back');
    back.setAttribute('type', 'button');
    back.setAttribute('data-se-back', '');
    back.textContent = '← All results';
    head.appendChild(back);

    var count = ce('span', 'se-groupview__count');
    count.textContent = (TYPE_LABEL[groupType] || groupType) + ' · ' + groupCount;
    head.appendChild(count);
    frag.appendChild(head);

    var list = ce('div', 'se-group');
    for (var i = 0; i < groupItems.length; i++) {
      list.appendChild(resultEl(groupItems[i]));
    }
    frag.appendChild(list);

    if (groupItems.length < groupCount) {
      var more = ce('button', 'se-loadmore');
      more.setAttribute('type', 'button');
      more.setAttribute('data-se-more', '');
      more.textContent = 'Load more (' + (groupCount - groupItems.length) + ')';
      frag.appendChild(more);
    }
    setResults(frag);
  }

  // ── a single result row ──────────────────────────────────────────────────
  function resultEl(item) {
    item = item || {};
    var a = ce('a', 'se-result se-result--' + (item.type || 'result'));
    a.setAttribute('href', item.url || '#');

    if (item.image) {
      var thumb = ce('span', 'se-result__thumb');
      var img = ce('img');
      img.setAttribute('src', item.image);
      img.setAttribute('alt', '');
      img.setAttribute('loading', 'lazy');
      thumb.appendChild(img);
      a.appendChild(thumb);
    }

    var body = ce('span', 'se-result__body');

    if (item.category || item.subbrand) {
      var meta = ce('span', 'se-result__meta');
      if (item.category) {
        var tag = ce('span', 'se-result__tag');
        tag.textContent = item.category;
        meta.appendChild(tag);
      }
      if (item.subbrand === 'rise' || item.subbrand === 'drift') {
        var badge = ce('span', 'se-result__badge se-result__badge--' + item.subbrand);
        badge.textContent = item.subbrand;
        meta.appendChild(badge);
      }
      body.appendChild(meta);
    }

    var title = ce('span', 'se-result__title');
    title.textContent = item.title || '';
    body.appendChild(title);

    if (item.snippet) {
      var snippet = ce('span', 'se-result__snippet');
      snippet.textContent = item.snippet;
      body.appendChild(snippet);
    }

    a.appendChild(body);
    return a;
  }

  // ── view toggles ─────────────────────────────────────────────────────────
  function setResults(node) {
    if (!results) return;
    results.innerHTML = '';
    results.appendChild(node);
    if (empty) empty.hidden = true;
    results.hidden = false;
  }

  function showEmpty(msg) {
    if (results) {
      results.innerHTML = '';
      results.hidden = true;
    }
    if (empty) empty.hidden = false;
    if (emptyMsg) emptyMsg.textContent = msg;
  }

  function renderError() {
    // Keep the modal usable; surface a quiet, non-blocking message.
    showEmpty('Search is unavailable right now. Please try again.');
  }

  // ── focus trap ───────────────────────────────────────────────────────────
  function focusables() {
    if (!dialog) return [];
    var nodes = dialog.querySelectorAll(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    var out = [];
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      // skip elements that are not currently rendered/visible
      if (n.offsetParent !== null || n === document.activeElement) out.push(n);
    }
    return out;
  }

  function trapTab(e) {
    var f = focusables();
    if (!f.length) return;
    var first = f[0];
    var last = f[f.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first || !dialog.contains(document.activeElement)) {
        e.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // ── input / event wiring ─────────────────────────────────────────────────
  function onInput() {
    var q = input.value.trim();
    if (debounceTimer) window.clearTimeout(debounceTimer);

    // Any edit returns us to the overview from a "see all" view.
    if (groupType !== null) exitGroup();

    if (q.length < MIN_QUERY_LEN) {
      seq++; // invalidate any in-flight response
      showEmpty(q.length === 0 ? DEFAULT_HINT : 'Keep typing…');
      return;
    }
    debounceTimer = window.setTimeout(function () {
      runOverview(q);
    }, DEBOUNCE_MS);
  }

  function onResultsClick(e) {
    var target = e.target;

    var back = closestMatch(target, '[data-se-back]');
    if (back) {
      e.preventDefault();
      exitGroup();
      if (lastQuery && lastQuery.length >= MIN_QUERY_LEN) runOverview(lastQuery);
      else showEmpty(DEFAULT_HINT);
      return;
    }

    var more = closestMatch(target, '[data-se-more]');
    if (more) {
      e.preventDefault();
      fetchGroupPage();
      return;
    }

    var seeAll = closestMatch(target, '[data-se-seeall]');
    if (seeAll) {
      e.preventDefault();
      enterGroup(seeAll.getAttribute('data-se-seeall'));
      return;
    }

    // A result selection IS a committed search — log it + beacon, then let the
    // anchor navigate (keepalive carries both requests through the unload).
    var anchor = closestMatch(target, 'a.se-result');
    if (anchor) {
      var source = sourceTag();
      logCommitted(lastQuery, source);
      emitSearchPerformed(lastQuery, lastTotal, source);
      // no preventDefault — navigation proceeds
    }
  }

  function isTypingTarget(el) {
    if (!el) return false;
    var tag = (el.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
    if (el.isContentEditable) return true;
    return false;
  }

  function onGlobalKeydown(e) {
    // "/" opens the modal from anywhere (unless the user is typing in a field).
    if (!isOpen()) {
      if (e.key === '/' && !isTypingTarget(e.target) && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        open();
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'Tab') {
      trapTab(e);
    }
  }

  function onInputKeydown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    }
  }

  // Delegated opener — the S9 header button (and any future trigger) just needs
  // [data-search-everything-open]; no per-button wiring required.
  function onDocumentClick(e) {
    var trigger = closestMatch(e.target, '[data-search-everything-open]');
    if (trigger) {
      e.preventDefault();
      open();
    }
  }

  // ── init ─────────────────────────────────────────────────────────────────
  function init() {
    root = qs('[data-search-everything]');
    if (!root) return; // snippet not on the page — nothing to wire

    overlay = qs('[data-se-overlay]', root);
    dialog = qs('[role="dialog"]', root);
    input = qs('[data-se-input]', root);
    results = qs('[data-se-results]', root);
    empty = qs('[data-se-empty]', root);
    emptyMsg = qs('[data-se-empty-msg]', root);

    if (input) {
      input.addEventListener('input', onInput);
      input.addEventListener('keydown', onInputKeydown);
    }
    if (results) results.addEventListener('click', onResultsClick);
    if (overlay) overlay.addEventListener('click', close);

    var closeBtn = qs('[data-se-close]', root);
    if (closeBtn) closeBtn.addEventListener('click', close);

    document.addEventListener('keydown', onGlobalKeydown);
    document.addEventListener('click', onDocumentClick);
  }

  // Expose a small API (shared window.Circadia namespace). S9's button can also
  // just use [data-search-everything-open]; this is for programmatic callers.
  if (!window.Circadia) window.Circadia = {};
  window.Circadia.search = { open: open, close: close, toggle: toggle };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
